/**
 * Create or edit a plan deliverable.
 *
 * These rows are what a cycle expands into each period, so the fields here are
 * the operational contract: what gets done, how often, roughly how long it
 * takes, who does it, and which gates it passes through.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type PlanItem } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const FREQUENCIES = ['monthly', 'weekly', 'daily', 'quarterly', 'semi_annual', 'annual', 'custom'];
const ROLES = ['intern', 'user', 'manager', 'admin'];

export function PlanItemDialog({ planId, item, open, onOpenChange }: {
  planId: string;
  item?: PlanItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [intervalDays, setIntervalDays] = useState('');
  const [dueDayOffset, setDueDayOffset] = useState('0');
  const [estimatedHours, setEstimatedHours] = useState('0.5');
  const [role, setRole] = useState('user');
  const [isIncluded, setIsIncluded] = useState(true);
  const [requiresReview, setRequiresReview] = useState(true);
  const [requiresClient, setRequiresClient] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setInstructions(item?.instructions ?? '');
    setAcceptance(item?.acceptance_criteria ?? '');
    setFrequency(item?.frequency ?? 'monthly');
    setIntervalDays(item?.interval_days != null ? String(item.interval_days) : '');
    setDueDayOffset(String(item?.due_day_offset ?? 0));
    setEstimatedHours(String(item?.estimated_hours ?? 0.5));
    setRole(item?.responsible_role ?? 'user');
    setIsIncluded(item?.is_included ?? true);
    setRequiresReview(item?.requires_internal_review ?? true);
    setRequiresClient(item?.requires_client_approval ?? false);
  }, [open, item]);

  const save = useMutation({
    mutationFn: () =>
      api.upsertPlanItem({
        itemId: item?.id,
        planId,
        name,
        instructions: instructions || null,
        acceptanceCriteria: acceptance || null,
        frequency,
        intervalDays: frequency === 'custom' && intervalDays ? Number(intervalDays) : null,
        dueDayOffset: Number(dueDayOffset) || 0,
        estimatedHours: Number(estimatedHours) || 0,
        responsibleRole: role,
        isIncluded,
        // The server refuses an item that is both covered by the subscription
        // and separately authorizable, so this mirrors that rule rather than
        // offering a combination that would be rejected.
        requiresAuthorization: !isIncluded,
        requiresInternalReview: requiresReview,
        requiresClientApproval: requiresClient,
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not save', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: item ? 'Deliverable updated' : 'Deliverable added' });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit deliverable' : 'Add deliverable'}</DialogTitle>
          <DialogDescription>
            One row of the playbook. Each period, this becomes a task with these instructions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              className="mt-1" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Google Business Profile post"
            />
          </div>

          <div>
            <Label className="text-xs">Instructions</Label>
            <Textarea
              className="mt-1" rows={5} value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={'1. Review the approved keyword list.\n2. Draft a post referencing one service area.\n3. Publish and screenshot.'}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Written once here, shown on every generated task. This is what removes the need to
              re-explain the process each month.
            </p>
          </div>

          <div>
            <Label className="text-xs">What "done" means</Label>
            <Textarea
              className="mt-1" rows={2} value={acceptance}
              onChange={(e) => setAcceptance(e.target.value)}
              placeholder="Post is live, mentions a real service, and a screenshot is attached."
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f} value={f} className="capitalize">
                      {f.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {frequency === 'custom' ? (
              <div>
                <Label className="text-xs">Every N days</Label>
                <Input
                  className="mt-1" type="number" min={1} max={365}
                  value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)}
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs">Due day of cycle</Label>
                <Input
                  className="mt-1" type="number" min={0} max={365}
                  value={dueDayOffset} onChange={(e) => setDueDayOffset(e.target.value)}
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Estimated hours</Label>
              <Input
                className="mt-1" type="number" min={0} step="0.25"
                value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground mt-1">Planning figure, not a target.</p>
            </div>
          </div>

          <div>
            <Label className="text-xs">Performed by</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Tasks for <em>intern</em> or <em>user</em> are auto-assigned to the engagement's
              assignee; others are left unassigned for you to pick up.
            </p>
          </div>

          <div className="space-y-2 border-t pt-3">
            <Check
              checked={isIncluded} onChange={setIsIncluded}
              label="Covered by the subscription fee"
              hint="Unchecked means it is out of scope and needs client authorization before it can be billed."
            />
            <Check
              checked={requiresReview} onChange={setRequiresReview}
              label="Requires your review before it counts as done"
            />
            <Check
              checked={requiresClient} onChange={setRequiresClient}
              label="Requires client approval"
              hint="Leave off for routine work so it does not queue behind an approval it never needed."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!name || save.isPending}>
            {item ? 'Save' : 'Add deliverable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Check({ checked, onChange, label, hint }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-input"
      />
      <span>
        <span className="text-sm">{label}</span>
        {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}
